import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Loader } from '../../components';
import { useContracts } from '../../hooks/useContracts';
import { ContractApiResponse } from '../../services/contract-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { NavigationConfig } from '../../utils/navigation-constants';

const OPENINVOICES_CONTRACTID_STORAGE_KEY = 'openinvoices_pending_contractid';

// Helper functions to use localStorage instead of sessionStorage
const storePendingContractId = (contractId: string) => {
    localStorage.setItem(OPENINVOICES_CONTRACTID_STORAGE_KEY, contractId);
};

const getPendingContractId = () => {
    return localStorage.getItem(OPENINVOICES_CONTRACTID_STORAGE_KEY);
};

const clearPendingContractId = () => {
    localStorage.removeItem(OPENINVOICES_CONTRACTID_STORAGE_KEY);
};

// Case-insensitive parameter lookup
const getSearchParam = (searchParams: URLSearchParams, paramName: string): string | null => {
    for (const [key, value] of searchParams.entries()) {
        if (key.toLowerCase() === paramName.toLowerCase()) {
            return value;
        }
    }
    return null;
};

const OpenInvoicesLink: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const { fetchContracts } = useContracts();

    useEffect(() => {
        // Check URL params first, then fall back to localStorage (for post-login case)
        let contractId = getSearchParam(searchParams, 'contractID');
        // Trim whitespace and trailing slashes
        if (contractId) {
            contractId = String(contractId).trim().replace(/\/+$/, '');
        }

        if (!contractId) {
            contractId = getPendingContractId();
            if (!contractId) {
                navigate(NavigationConfig.Payments, { replace: true });
                return;
            }
        }

        if (!isLoggedIn) {
            storePendingContractId(String(contractId));
            // Redirect to login (usePendingLinkRedirect hook will handle redirect after login)
            navigate(NavigationConfig.Login);
            return;
        }

        // User is logged in, fetch contracts and validate the contractID
        const fetchAndOpenOpenInvoices = async () => {
            try {
                dispatch(showLoader());
                const response = await fetchContracts(false);

                if (response.statusCode === 200 && response.data && response.data.length > 0) {
                    // Find contract matching the contractID (match against AspireId, the user-facing ID)
                    const matchingContract = response.data.find(
                        (contract: ContractApiResponse) => String(contract.AspireId) === String(contractId),
                    );

                    if (matchingContract) {
                        // Clear localStorage and navigate to Payments with open-invoices tab and contract
                        clearPendingContractId();
                        navigate(NavigationConfig.Payments, {
                            replace: true,
                            state: {
                                selectedTab: 'open-invoices',
                                selectedContract: matchingContract,
                            },
                        });
                    } else {
                        // Clear localStorage to prevent redirect loop
                        clearPendingContractId();
                        dispatch(
                            openToast({
                                show: true,
                                isAlertOpen: true,
                                message: 'Contract not found',
                                severity: 'error',
                            }),
                        );
                        navigate(NavigationConfig.Payments, { replace: true });
                    }
                } else {
                    // Clear localStorage to prevent redirect loop
                    clearPendingContractId();
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: response.message || 'Failed to load contracts',
                            severity: 'error',
                        }),
                    );
                    navigate(NavigationConfig.Payments, { replace: true });
                }
            } catch (err) {
                // Clear localStorage to prevent redirect loop
                clearPendingContractId();
                console.error('Error fetching contracts for OpenInvoicesLink:', err);
                const e = err as { message?: string; response?: { data?: { message?: string } } };
                const errorMessage =
                    e?.response?.data?.message || e?.message || 'Failed to load contracts. Please try again.';
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: errorMessage,
                        severity: 'error',
                    }),
                );
                navigate(NavigationConfig.Payments, { replace: true });
            } finally {
                dispatch(hideLoader());
            }
        };

        fetchAndOpenOpenInvoices();
    }, [searchParams, navigate, isLoggedIn, dispatch, fetchContracts]);

    // Show loader while processing
    return <Loader />;
};

export { OpenInvoicesLink, OPENINVOICES_CONTRACTID_STORAGE_KEY };
